'use client';

import React, { useState, useEffect } from 'react';
import { setConsent } from '../../lib/analytics';

interface CookieConsentProps {
  className?: string;
}

export function CookieConsent({ className = '' }: CookieConsentProps) {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Banner nur anzeigen, wenn keine Entscheidung gespeichert ist
    const consentStatus = localStorage.getItem('analytics-consent');
    if (consentStatus === null) {
      setShowBanner(true);
    } else {
      // Consent bereits gesetzt, also im GA umsetzen
      setConsent(consentStatus === 'true');
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('analytics-consent', 'true');
    setConsent(true);
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('analytics-consent', 'false');
    setConsent(false);
    setShowBanner(false);
  };

  const resetConsent = () => {
    localStorage.removeItem('analytics-consent');
    setShowBanner(true);
  };

  if (!showBanner) {
    return (
      <button
        onClick={resetConsent}
        className="text-xs text-orange-600 hover:underline fixed bottom-2 right-2 z-50 bg-white/90 p-1.5 rounded-md shadow-sm border border-orange-100"
        aria-label="Cookie-Einstellungen anpassen"
      >
        Cookie-Einstellungen
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 bg-orange-50 shadow-lg z-50 p-4 md:p-6 border-t border-orange-200 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-bold text-orange-800">Wir verwenden Cookies</h2>
            <p className="mt-2 text-gray-700">
              Diese Website verwendet Google Analytics und Firebase, um anonymisierte Nutzungsdaten zu sammeln. 
              Dies hilft uns, die Benutzerfreundlichkeit zu verbessern und unsere Dienstleistungen zu optimieren. 
              Ihre IP-Adresse wird anonymisiert und die Daten werden nicht mit anderen Informationen zusammengeführt.
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Weitere Informationen finden Sie in unserer{' '}
              <a href="#datenschutz" className="text-orange-600 hover:underline font-medium">
                Datenschutzerklärung
              </a>.
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={handleDecline}
              className="px-4 py-2 border border-orange-300 rounded-md text-orange-700 hover:bg-orange-100 transition-colors"
              aria-label="Cookies ablehnen"
            >
              Ablehnen
            </button>
            <button
              onClick={handleAccept}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              aria-label="Cookies akzeptieren"
            >
              Akzeptieren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Komponente zum Zurücksetzen des Consent-Status
export function ResetCookieConsent() {
  function handleReset() {
    localStorage.removeItem('analytics-consent');
    window.location.reload(); // Seite neu laden, um Banner anzuzeigen
  }

  return (
    <button
      onClick={handleReset}
      className="text-sm text-orange-600 hover:underline"
    >
      Cookie-Einstellungen zurücksetzen
    </button>
  );
}

export default CookieConsent 